const PAGE_TEXT_WIDTH_TWIPS = 9020;
const LEFT_COLUMN_WIDTH_TWIPS = 6000;
const RIGHT_COLUMN_WIDTH_TWIPS = PAGE_TEXT_WIDTH_TWIPS - LEFT_COLUMN_WIDTH_TWIPS;
const CELL_MARGINS = {
  top: 160,
  bottom: 160,
  left: 180,
  right: 180,
};
const INSIGHT_TYPE_LABELS = {
  0: "Instruction",
  1: "Information",
  2: "Assessment",
};
let docxModulePromise = null;

const loadDocxModule = () => {
  if (!docxModulePromise) {
    docxModulePromise = import("docx");
  }

  return docxModulePromise;
};

const getFirstTextValue = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return "";
};

const normalizeMultilineText = (value = "") =>
  String(value).replace(/\r\n/g, "\n").replace(/\r/g, "\n").trim();

const slugifyFilename = (value = "guide") => {
  const slug = String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return slug || "guide";
};

const getEnvironmentVisitedTrigger = (insight) =>
  Array.isArray(insight?.insightTriggers)
    ? insight.insightTriggers.find((trigger) => Number(trigger?.insightTriggerType) === 1)
    : null;

export const groupGuideInsightsForDocxExport = (insights = []) => {
  const groups = [];
  let currentGroup = null;

  (Array.isArray(insights) ? insights : []).forEach((insight, index) => {
    const environmentTrigger = getEnvironmentVisitedTrigger(insight);

    if (environmentTrigger || !currentGroup) {
      currentGroup = {
        key: `${environmentTrigger?.triggerId || "guide-start"}-${index}`,
        environmentId: environmentTrigger?.triggerId || "",
        triggerName: environmentTrigger?.name || "",
        startIndex: index,
        insights: [],
      };
      groups.push(currentGroup);
    }

    currentGroup.insights.push({ ...insight, originalIndex: index });
  });

  return groups;
};

const getEnvironmentSourceFromSystem = (systemData, environmentId) => {
  if (!systemData || !environmentId) {
    return null;
  }

  const collections = [
    systemData.environments,
    systemData.environmentDatas,
    systemData.environmentData,
  ];

  for (const collection of collections) {
    if (Array.isArray(collection)) {
      const found = collection.find(
        (environment) =>
          getFirstTextValue(
            environment?.id,
            environment?.environmentId,
            environment?.environmentID
          ) === environmentId
      );

      if (found) {
        return found;
      }
    }

    if (collection && typeof collection === "object") {
      const direct = collection[environmentId];
      if (direct) {
        return direct;
      }
    }
  }

  return null;
};

const resolveEnvironmentDetails = ({ environmentId, triggerName, environments, systemData }) => {
  const option =
    (Array.isArray(environments) ? environments : []).find(
      (environment) => environment?.id === environmentId
    ) || {};
  const source = getEnvironmentSourceFromSystem(systemData, environmentId) || {};
  const metadata = source.metadata || {};
  const labelData = source.labelData || {};

  return {
    id: environmentId,
    name:
      getFirstTextValue(
        option.name,
        source.environmentName,
        source.name,
        metadata.name,
        source.title,
        metadata.title,
        triggerName
      ) || "Guide Start",
    description: getFirstTextValue(
      option.description,
      source.environmentDescription,
      source.description,
      source.summary,
      source.prompt,
      metadata.description,
      metadata.summary,
      labelData.description,
      labelData.body,
      labelData.text
    ),
  };
};

const getImageTypeFromBlob = (blob, sourceUrl = "") => {
  const type = blob?.type || "";
  const source = `${type} ${sourceUrl.split("?")[0]}`.toLowerCase();

  if (source.includes("jpeg") || source.includes(".jpg") || source.includes(".jpeg")) {
    return "jpg";
  }

  if (source.includes("png") || source.includes(".png")) {
    return "png";
  }

  if (source.includes("gif") || source.includes(".gif")) {
    return "gif";
  }

  if (source.includes("bmp") || source.includes(".bmp")) {
    return "bmp";
  }

  return "";
};

const fetchImageData = async (sourceUrl) => {
  if (!sourceUrl || typeof fetch !== "function") {
    return null;
  }

  try {
    const response = await fetch(sourceUrl, { credentials: "omit" });
    if (!response.ok) {
      throw new Error(`Thumbnail request failed with status ${response.status}`);
    }

    const blob = await response.blob();
    const type = getImageTypeFromBlob(blob, sourceUrl);

    if (!type) {
      return null;
    }

    return {
      type,
      data: await blob.arrayBuffer(),
    };
  } catch (error) {
    console.warn("Unable to embed guide export thumbnail.", error);
    return null;
  }
};

const getTableBorders = (docx) => {
  const subtleBorder = {
    style: docx.BorderStyle.SINGLE,
    size: 1,
    color: "D8DEE8",
  };
  const rowDivider = {
    style: docx.BorderStyle.SINGLE,
    size: 8,
    color: "8A94A6",
  };

  return {
    subtleBorder,
    rowDivider,
  };
};

const paragraph = (docx, text, options = {}) => {
  const { Paragraph, TextRun } = docx;

  return new Paragraph({
    children: [new TextRun({ text, ...options.run })],
    spacing: { after: options.after ?? 80, before: options.before ?? 0 },
    alignment: options.alignment,
  });
};

const labeledParagraphs = (docx, label, value, runOptions = {}) => {
  const { Paragraph, TextRun } = docx;
  const normalizedValue = normalizeMultilineText(value);

  if (!normalizedValue) {
    return [];
  }

  return normalizedValue.split("\n").map(
    (line, index) =>
      new Paragraph({
        children: [
          ...(index === 0
            ? [new TextRun({ text: `${label}: `, bold: true, ...runOptions })]
            : []),
          new TextRun({ text: line || " ", ...runOptions }),
        ],
        spacing: { after: 80 },
      })
  );
};

const insightParagraphs = (docx, insight) => {
  const { Paragraph, TextRun } = docx;
  const typeLabel = INSIGHT_TYPE_LABELS[Number(insight?.insightType)] || "Insight";
  const name = getFirstTextValue(insight?.name);
  const headingPrefix = `${Number(insight?.originalIndex ?? 0) + 1}. [${typeLabel}]`;
  const headingText = name ? `${headingPrefix} ${name}` : headingPrefix;
  const children = [
    new Paragraph({
      children: [new TextRun({ text: headingText, bold: true, size: 22 })],
      spacing: { before: 120, after: 60 },
    }),
    ...labeledParagraphs(docx, "Script", insight?.text, { size: 20 }),
    ...labeledParagraphs(docx, "Answer key", insight?.answerKey, { size: 20 }),
    ...labeledParagraphs(docx, "Topics", insight?.topics, { size: 20 }),
  ];

  if (insight?.id) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: `Insight ID: ${insight.id}`, color: "6B7280", size: 16 })],
        spacing: { after: 120 },
      })
    );
  }

  return children;
};

const createHeaderRow = (docx) => {
  const { ShadingType, TableCell, TableRow, WidthType } = docx;

  return new TableRow({
    tableHeader: true,
    children: [
      new TableCell({
        width: { size: LEFT_COLUMN_WIDTH_TWIPS, type: WidthType.DXA },
        margins: CELL_MARGINS,
        shading: { type: ShadingType.CLEAR, fill: "EEF4F8", color: "auto" },
        children: [paragraph(docx, "Insights", { run: { bold: true }, after: 0 })],
      }),
      new TableCell({
        width: { size: RIGHT_COLUMN_WIDTH_TWIPS, type: WidthType.DXA },
        margins: CELL_MARGINS,
        shading: { type: ShadingType.CLEAR, fill: "EEF4F8", color: "auto" },
        children: [paragraph(docx, "Environment", { run: { bold: true }, after: 0 })],
      }),
    ],
  });
};

const createEnvironmentCellChildren = async ({ docx, details, thumbnailUrl }) => {
  const { AlignmentType, ImageRun, Paragraph, TextRun } = docx;
  const imageData = await fetchImageData(thumbnailUrl);
  const children = [];

  if (imageData) {
    children.push(
      new Paragraph({
        children: [
          new ImageRun({
            data: imageData.data,
            type: imageData.type,
            transformation: { width: 180, height: 180 },
            altText: {
              name: details.name,
              title: `${details.name} thumbnail`,
              description: `Thumbnail for ${details.name}`,
            },
          }),
        ],
        alignment: AlignmentType.CENTER,
        spacing: { after: 140 },
      })
    );
  }

  children.push(
    new Paragraph({
      children: [new TextRun({ text: details.name, bold: true, size: 22 })],
      spacing: { after: 80 },
    })
  );

  if (details.description) {
    children.push(...labeledParagraphs(docx, "Description", details.description, { size: 20 }));
  }

  if (details.id) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: `Environment ID: ${details.id}`, color: "6B7280", size: 16 })],
        spacing: { after: 0 },
      })
    );
  }

  return children;
};

const createEnvironmentRow = async ({
  docx,
  group,
  environments,
  systemData,
  thumbnailUrls,
}) => {
  const { Paragraph, TableCell, TableRow, TextRun, VerticalAlign, WidthType } = docx;
  const details = resolveEnvironmentDetails({
    environmentId: group.environmentId,
    triggerName: group.triggerName,
    environments,
    systemData,
  });
  const leftChildren = [
    new Paragraph({
      children: [
        new TextRun({
          text: details.name,
          bold: true,
          size: 24,
        }),
      ],
      spacing: { after: 120 },
    }),
    ...group.insights.flatMap((insight) => insightParagraphs(docx, insight)),
  ];
  const rightChildren = await createEnvironmentCellChildren({
    docx,
    details,
    thumbnailUrl: thumbnailUrls?.[group.environmentId],
  });

  return new TableRow({
    children: [
      new TableCell({
        width: { size: LEFT_COLUMN_WIDTH_TWIPS, type: WidthType.DXA },
        margins: CELL_MARGINS,
        verticalAlign: VerticalAlign.TOP,
        children: leftChildren.length ? leftChildren : [paragraph(docx, "No insights")],
      }),
      new TableCell({
        width: { size: RIGHT_COLUMN_WIDTH_TWIPS, type: WidthType.DXA },
        margins: CELL_MARGINS,
        verticalAlign: VerticalAlign.TOP,
        children: rightChildren,
      }),
    ],
  });
};

export const buildGuideDocx = async ({
  guide,
  environments = [],
  systemData = null,
  thumbnailUrls = {},
} = {}) => {
  const docx = await loadDocxModule();
  const {
    BorderStyle,
    Document,
    HeadingLevel,
    Paragraph,
    Table,
    TableLayoutType,
    TextRun,
    WidthType,
  } = docx;
  const { subtleBorder, rowDivider } = getTableBorders({ BorderStyle });
  const guideName = getFirstTextValue(guide?.name) || "Guide";
  const groups = groupGuideInsightsForDocxExport(guide?.insightDatas);
  const rows = await Promise.all(
    groups.map((group) =>
      createEnvironmentRow({ docx, group, environments, systemData, thumbnailUrls })
    )
  );

  return new Document({
    title: guideName,
    creator: "10k Science",
    description: "Guide language export",
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: 720,
              right: 720,
              bottom: 720,
              left: 720,
            },
          },
        },
        children: [
          new Paragraph({
            text: guideName,
            heading: HeadingLevel.TITLE,
            spacing: { after: 160 },
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: "Exported for editorial review. Edit script, answer key, topics, and environment notes as needed.",
                color: "4B5563",
                size: 20,
              }),
            ],
            spacing: { after: 260 },
          }),
          new Table({
            width: { size: PAGE_TEXT_WIDTH_TWIPS, type: WidthType.DXA },
            columnWidths: [LEFT_COLUMN_WIDTH_TWIPS, RIGHT_COLUMN_WIDTH_TWIPS],
            layout: TableLayoutType.FIXED,
            borders: {
              top: subtleBorder,
              bottom: subtleBorder,
              left: subtleBorder,
              right: subtleBorder,
              insideHorizontal: rowDivider,
              insideVertical: subtleBorder,
            },
            rows: [createHeaderRow(docx), ...rows],
          }),
        ],
      },
    ],
  });
};

export const downloadGuideDocx = async (options = {}) => {
  if (typeof document === "undefined" || typeof URL === "undefined") {
    throw new Error("DOCX export is only available in the browser.");
  }

  const documentFile = await buildGuideDocx(options);
  const { Packer } = await loadDocxModule();
  const blob = await Packer.toBlob(documentFile);
  const fileName = `${slugifyFilename(options.guide?.name)}.docx`;
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();

  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
};
